/* 星环实验场 SC-RING：老板展示大屏。
   中央 9 关星环缓慢轮转，在筛结构骑在环上并带短彗尾；每过一关，该关的真实
   计算指标（gap / E_hull / λ / Tc…）化作彗星粒子向外飞出；结构不稳 → 沿环
   法线红色外飞 + 扩散环；结果稳定 → 螺旋收进中央入库核心 + 金环冲击波。
   中心视觉（编织星环 / 计算球面 / CORE 网格）参考 BCS 星环组件。
   数据全部来自现有 REST 接口（轮询 diff 出"事件"），演示模式另有仿真。 */
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {apiGet, usePolling} from '../api';
import {StructureModal} from '../MoleculeChip';

const STAGES = ['S1_symmetry', 'S2_alignn_gap', 'S3_vasp_relax',
  'S4_vasp_magnetic', 'S5_mp_hull', 'S6_prophet_phonons', 'S7_qe_relax_pm',
  'S8_qe_phonons', 'S9_epw_tc'];
const STAGE_SHORT = ['S1 对称性', 'S2 AI带隙', 'S3 VASP精修',
  'S4 磁性', 'S5 能量稳定', 'S6 AI声子', 'S7 第一性精算',
  'S8 声子验证', 'S9 EPW Tc'];
const STAGE_HUMAN = {
  S1_symmetry: '结构预检查', S2_alignn_gap: 'AI 带隙初筛', S3_vasp_relax: '结构精修',
  S4_vasp_magnetic: '磁性检查', S5_mp_hull: '能量稳定性检查',
  S6_prophet_phonons: 'AI 声子预筛', S7_qe_relax_pm: '第一性原理精算',
  S8_qe_phonons: '声子稳定性验证', S9_epw_tc: '超导温度计算',
};

const LABEL_HUMAN = {
  TC_REVIEW_COMPLETE: '闯完 9 关，超导候选',
  STRUCTURE_REJECT_SINGLE_ELEMENT: '单质结构，淘汰',
  SINGLE_ELEMENT_REJECT: '单质结构，淘汰',
  STRUCTURE_REJECT_F_ELECTRON: '含 f 电子，淘汰',
  GAP_REJECT: 'AI 判金属，淘汰',
  PROPHET_UNSTABLE: '结构不稳定',
  PROPHET_SOFT_MODE_SUSPECT: '软模迹象，结构可能不稳',
  QE_PHONON_UNSTABLE: '声子验证不通过，结构不稳',
  HULL_REJECT: '能量不稳，易分解',
  MAGNETIC_REJECT: '磁性太强，淘汰',
  PM_REJECT: '电声耦合不达标，淘汰',
  MANUAL_REJECT: '人工审查后放弃',
  TC_INCOMPLETE: 'Tc 计算不完整',
  AUTO_DISCARD_FIX_BUDGET: '多次修复仍失败，自动淘汰',
  AUTO_DISCARD_DIAG: '无修复方向，自动淘汰',
  AUTO_DISCARD_POISON: '同类失败反复，自动淘汰',
  AUTO_DISCARD_RUN_ENDED: '批次结束状态丢失，自动淘汰',
  AUTO_DISCARD_PROBE_FAIL: '结果核验不成立，自动淘汰',
};
const labelHuman = l => LABEL_HUMAN[l] ||
  (l && l.startsWith('AUTO_DISCARD_') ? '自动修复仍失败，淘汰' : null);

/* 阶段 metrics → 一行展示文本（真实数值，来自 /candidates/{id} detail） */
const fmtNum = (v, d = 3) => {
  if (v == null || Number.isNaN(Number(v))) return null;
  const n = Number(v);
  return Math.abs(n) >= 1000 ? n.toFixed(0) : n.toFixed(d).replace(/\.?0+$/, '');
};
const THZ_TO_CM1 = 33.35641;
const METRIC_FMT = [
  [/^S1/, m => m.spacegroup_after
    ? `空间群 ${m.spacegroup_after} · #${m.spacegroup_after_number}` : null],
  [/^S2/, m => m.gap_ev != null ? `gap ${fmtNum(m.gap_ev)} eV` : null],
  [/^S3/, m => m.final_energy_ev != null
    ? `E = ${fmtNum(m.final_energy_ev, 4)} eV` : null],
  [/^S4/, m => m.delta_e_mag_mev_per_atom != null
    ? `ΔE_mag ${fmtNum(m.delta_e_mag_mev_per_atom, 2)} meV/at` : null],
  [/^S5/, m => m.energy_above_hull_ev_per_atom != null
    ? `E_hull +${fmtNum(m.energy_above_hull_ev_per_atom, 4)} eV/at` : null],
  [/^S6|^S7e/, m => m.minimum_frequency_thz != null
    ? `min ω ${fmtNum(m.minimum_frequency_thz * THZ_TO_CM1, 1)} cm⁻¹` : null],
  [/^S7g/, m => m.P_over_cell_mass != null
    ? `λ_P/M ${fmtNum(m.P_over_cell_mass, 2)}` : null],
  [/^S8/, m => m.global_minimum_cm1 != null
    ? `ω_min ${fmtNum(m.global_minimum_cm1, 1)} cm⁻¹` : null],
  [/^S9/, m => (m.tc && m.tc.tc_primary_K != null)
    ? `Tc ${Number(m.tc.tc_primary_K).toFixed(2)} K · λ ${fmtNum(m.tc.lambda_epw, 2)}`
    : null],
];
function metricText(stage, metrics) {
  if (!metrics) return null;
  for (const [re, fn] of METRIC_FMT) {
    if (re.test(stage)) {
      const t = fn(metrics);
      if (t) return t;
    }
  }
  return null;
}

const stageIndexOf = s => {
  const si = STAGES.indexOf(s);
  if (si !== -1) return si;
  const m = /^(S\d+)/.exec(s || '');
  return m ? STAGES.findIndex(x => x.startsWith(m[1] + '_')) : -1;
};

/* 确定性伪随机（散点星图用：同一候选每次刷新落点不变） */
function mulberry32(a) {
  return function() {
    a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/* ---------- 中心星环：倾斜 3D 投影（参考 BCS 星环组件） ----------
   环面按 a/b/c 三个倾斜角投影；ts 用秒（performance.now()/1000）。
   画布主循环与画布外的事件特效共用同一套几何，保证结构光点、
   标签和飞出的特效都落在视觉上的同一位置。 */
const TAU = Math.PI * 2;
const RING_RATIO = 0.28;
/* 参考组件的动画时钟比真实秒快约 1.45×；画布内外统一用这个时钟 */
const RING_TEMPO = 1.45;
const animTs = ms => ms / 1000 * RING_TEMPO;
/* 参考组件的 9 关从左侧偏下起排；关卡盘转到这里，标签位置和参考一致 */
const STAGE_START = -Math.PI * 0.95;
const ringGeom = (w, h) => ({
  cx: w / 2, cy: h * 0.46, R: Math.min(w, h) * RING_RATIO,
});
function makeProject(w, h, ts) {
  const {cx, cy} = ringGeom(w, h);
  const a = 0.82 + Math.sin(ts * 0.07) * 0.13;
  const b = 0.34 + Math.sin(ts * 0.052) * 0.16;
  const c = -0.30 + Math.sin(ts * 0.04) * 0.09;
  const ca = Math.cos(a), sa = Math.sin(a);
  const cb = Math.cos(b), sb = Math.sin(b);
  const cc = Math.cos(c), sc = Math.sin(c);
  return (x, y, z) => {
    const y1 = y * ca - z * sa;
    const z1 = y * sa + z * ca;
    const x2 = x * cb + z1 * sb;
    const z2 = -x * sb + z1 * cb;
    const xx = x2 * cc - y1 * sc;
    const yy = x2 * sc + y1 * cc;
    const p = 1 / (1 + z2 * 0.00085);
    return {x: cx + xx * p, y: cy + yy * p, z: z2, p};
  };
}
function ringXY(ang, w, h, ts, radMul = 1, z = 0) {
  const {R} = ringGeom(w, h);
  return makeProject(w, h, ts)(Math.cos(ang) * R * radMul,
                               Math.sin(ang) * R * radMul, z);
}
function strokeLine(ctx, pts, color, width = 0.6) {
  ctx.beginPath();
  pts.forEach((p, i) => (i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)));
  ctx.strokeStyle = color;
  ctx.lineWidth = width;
  ctx.stroke();
}
function torusPoint(proj, a, b, R, r) {
  return proj((R + r * Math.cos(b)) * Math.cos(a),
              (R + r * Math.cos(b)) * Math.sin(a),
              r * Math.sin(b));
}
function orbitPt(proj, a, R, tilt = 0, phase = 0) {
  const x = Math.cos(a) * R, y = Math.sin(a) * R;
  return proj(x, y * Math.cos(tilt),
              y * Math.sin(tilt) + Math.sin(a * 3 + phase) * R * 0.018);
}
function glowDot(ctx, p, color = '204,184,249', size = 2.4) {
  const g = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, 15);
  g.addColorStop(0, `rgba(${color},.6)`);
  g.addColorStop(1, `rgba(${color},0)`);
  ctx.fillStyle = g;
  ctx.fillRect(p.x - 15, p.y - 15, 30, 30);
  ctx.fillStyle = `rgb(${color})`;
  ctx.beginPath();
  ctx.arc(p.x, p.y, size, 0, TAU);
  ctx.fill();
}
/* 结构光点量级到百个，柔光晕用预渲染 sprite，避免每帧 createRadialGradient */
const glowSpriteCache = new Map();
function glowSprite(color) {
  let c = glowSpriteCache.get(color);
  if (c) return c;
  c = document.createElement('canvas');
  c.width = c.height = 32;
  const g = c.getContext('2d');
  const grad = g.createRadialGradient(16, 16, 0, 16, 16, 16);
  grad.addColorStop(0, `rgba(${color},.7)`);
  grad.addColorStop(1, `rgba(${color},0)`);
  g.fillStyle = grad;
  g.fillRect(0, 0, 32, 32);
  glowSpriteCache.set(color, c);
  return c;
}
function glowMarker(ctx, p, color, size, alpha = 1) {
  ctx.globalAlpha = Math.max(0, alpha);
  ctx.drawImage(glowSprite(color), p.x - 16, p.y - 16, 32, 32);
  ctx.fillStyle = `rgb(${color})`;
  ctx.beginPath();
  ctx.arc(p.x, p.y, size, 0, TAU);
  ctx.fill();
  ctx.globalAlpha = 1;
}
/* 沿投影环向后采样的短彗尾：结构在环上行走 / 在算时的拖尾 */
function drawRingTrail(ctx, proj, ang, R, color, strength) {
  const pts = [];
  for (let k = 0; k <= 10; k++) {
    const a = ang - k * 0.013;
    pts.push(proj(Math.cos(a) * R, Math.sin(a) * R, 0));
  }
  strokeLine(ctx, pts, `rgba(${color},${strength})`, 1.2);
}

/* 中央计算球面：经线 / 纬线 / 扰动飘带 */
function drawSphere(ctx, proj, R, ts) {
  const sphere = R * 0.69, rot = ts * 0.055;
  for (let k = 0; k < 24; k++) {
    const lon = k / 24 * TAU + rot, pts = [];
    for (let j = 0; j <= 60; j++) {
      const a = j / 60 * TAU;
      pts.push(proj(Math.cos(a) * Math.cos(lon) * sphere,
                    Math.sin(a) * sphere,
                    Math.cos(a) * Math.sin(lon) * sphere));
    }
    strokeLine(ctx, pts, k % 4 === 0
      ? 'rgba(171,185,213,.22)' : 'rgba(170,174,211,.075)', 0.5);
  }
  for (let k = 1; k < 13; k++) {
    const lat = k / 13 * Math.PI - Math.PI / 2, pts = [];
    for (let j = 0; j <= 64; j++) {
      const a = j / 64 * TAU + rot;
      pts.push(proj(Math.cos(a) * Math.cos(lat) * sphere,
                    Math.sin(lat) * sphere,
                    Math.sin(a) * Math.cos(lat) * sphere));
    }
    strokeLine(ctx, pts, 'rgba(174,186,216,.16)', 0.45);
  }
  for (let k = 0; k < 20; k++) {
    const phase = k / 20 * TAU, pts = [];
    for (let j = 0; j <= 60; j++) {
      const a = j / 60 * TAU;
      const rad = sphere * (0.78 + 0.11 * Math.sin(a * 3 + phase + ts * 0.11));
      pts.push(proj(Math.cos(a) * rad,
                    Math.sin(a) * rad * Math.cos(phase),
                    Math.sin(a) * rad * Math.sin(phase)));
    }
    strokeLine(ctx, pts, k % 5 === 0
      ? 'rgba(177,145,230,.22)' : 'rgba(186,193,222,.08)', 0.5);
  }
}

/* 编织星环：3 层壳带 + 环管束 */
function drawBraidedRing(ctx, proj, R, r, ts) {
  for (let shell = 0; shell < 3; shell++) {
    const rr = R * (0.94 + shell * 0.062);
    const tube = r * (0.6 + shell * 0.2);
    for (let j = 0; j < 12; j++) {
      const beta = j / 12 * TAU + ts * 0.035 * (shell % 2 ? 1 : -1);
      const pts = [];
      for (let i = 0; i <= 120; i++) {
        const a = i / 120 * TAU;
        const z = tube * Math.sin(beta + a * 2 + ts * 0.035);
        pts.push(proj((rr + tube * Math.cos(beta)) * Math.cos(a),
                      (rr + tube * Math.cos(beta)) * Math.sin(a), z));
      }
      strokeLine(ctx, pts, `rgba(${shell === 1 ? '197,187,222'
        : '188,204,225'},${j % 3 === 0 ? 0.34 : 0.13})`,
      j % 3 === 0 ? 0.7 : 0.45);
    }
  }
  for (let j = 0; j < 108; j++) {
    const a = j / 108 * TAU + ts * 0.024, pts = [];
    for (let k = 0; k <= 16; k++)
      pts.push(torusPoint(proj, a, k / 16 * TAU, R, r));
    strokeLine(ctx, pts,
               `rgba(207,218,237,${pts[0].z > 0 ? 0.36 : 0.13})`, 0.5);
  }
}

/* 3 条独立彗星轨道，横切环面 */
function drawCometOrbits(ctx, proj, R, ts) {
  const layers = [
    {tilt: 0.74, rr: R * 1.16, dir: 0.095, color: '178,215,247'},
    {tilt: -0.64, rr: R * 1.23, dir: -0.072, color: '209,179,255'},
    {tilt: 1.18, rr: R * 1.09, dir: 0.064, color: '178,207,235'},
  ];
  layers.forEach((L, layer) => {
    const pts = [];
    for (let j = 0; j <= 140; j++)
      pts.push(orbitPt(proj, j / 140 * TAU, L.rr, L.tilt));
    strokeLine(ctx, pts, layer === 1
      ? 'rgba(183,161,229,.34)' : 'rgba(178,207,235,.25)', 0.65);
    const head = ts * L.dir + layer * 2;
    for (let k = 0; k < 28; k++) {
      const trail = [];
      for (let j = 0; j <= 4; j++)
        trail.push(orbitPt(proj, head - (k + j / 4) * 0.011, L.rr, L.tilt));
      strokeLine(ctx, trail, `rgba(${L.color},${(1 - k / 28) * 0.7})`, 1.1);
    }
    glowDot(ctx, orbitPt(proj, head, L.rr, L.tilt),
            layer === 1 ? '209,179,255' : '192,222,248', 1.6);
  });
}

/* 外圈测量刻度环 */
function drawTickRing(ctx, proj, R) {
  for (let j = 0; j < 216; j++) {
    const a = j / 216 * TAU;
    const ro = j % 6 === 0 ? 1.32 : 1.292;
    strokeLine(ctx, [
      proj(Math.cos(a) * R * 1.28, Math.sin(a) * R * 1.28, 0),
      proj(Math.cos(a) * R * ro, Math.sin(a) * R * ro, 0),
    ], j % 6 === 0 ? 'rgba(188,201,224,.5)' : 'rgba(158,177,205,.2)', 0.6);
  }
}

/* 开放式 CORE 曲面：会呼吸的局部网格，中心留空给 CORE 文字 */
function drawCoreSurface(ctx, proj, R, ts) {
  const center = proj(0, 0, 0), radius = R * 0.52;
  const shade = ctx.createRadialGradient(center.x, center.y, 0,
                                         center.x, center.y, radius);
  shade.addColorStop(0, 'rgba(5,7,10,.94)');
  shade.addColorStop(0.35, 'rgba(5,7,10,.78)');
  shade.addColorStop(1, 'rgba(5,7,10,0)');
  ctx.fillStyle = shade;
  ctx.fillRect(center.x - radius, center.y - radius, radius * 2, radius * 2);
  const light = ctx.createRadialGradient(center.x, center.y, 0,
                                         center.x, center.y, R * 0.38);
  light.addColorStop(0, 'rgba(255,209,35,.055)');
  light.addColorStop(1, 'rgba(255,209,35,0)');
  ctx.fillStyle = light;
  ctx.fillRect(center.x - radius, center.y - radius, radius * 2, radius * 2);
  const point = (u, v) => {
    const x = u * R * 0.38, y = v * R * 0.22;
    const z = R * (0.09 * Math.sin(u * 2.5 - ts * 0.19) *
      Math.cos(v * 1.7 + ts * 0.12) + 0.055 * u * v);
    return proj(x, y, z);
  };
  const curve = (points, color, width) => {
    ctx.beginPath();
    let moving = false;
    for (const p of points) {
      // 中心区域跳过，保证 CORE 文字干净可读
      if (Math.abs(p.x - center.x) < 28 && Math.abs(p.y - center.y) < 9) {
        moving = false;
        continue;
      }
      if (moving) ctx.lineTo(p.x, p.y);
      else ctx.moveTo(p.x, p.y);
      moving = true;
    }
    ctx.strokeStyle = color;
    ctx.lineWidth = width;
    ctx.stroke();
  };
  for (let k = 0; k <= 15; k++) {
    const u = -1 + k / 15 * 2, pts = [];
    for (let j = 0; j <= 30; j++) pts.push(point(u, -1 + j / 30 * 2));
    curve(pts, k % 4 === 0 ? 'rgba(214,222,240,.46)'
      : 'rgba(178,180,217,.24)', k % 4 === 0 ? 0.65 : 0.45);
  }
  for (let k = 0; k <= 9; k++) {
    const v = -1 + k / 9 * 2, pts = [];
    for (let j = 0; j <= 48; j++) pts.push(point(-1 + j / 48 * 2, v));
    curve(pts, k % 3 === 0 ? 'rgba(205,193,238,.42)'
      : 'rgba(176,173,218,.20)', 0.5);
  }
  for (let k = 0; k < 3; k++) {
    const v = (k - 1) * 0.55;
    const head = ((ts * 0.08 + k * 0.33) % 1) * 2 - 1;
    const pts = [];
    for (let j = 0; j <= 11; j++) {
      const u = head - j * 0.012;
      if (u >= -1) pts.push(point(u, v));
    }
    curve(pts, k === 1 ? 'rgba(230,234,250,.7)'
      : 'rgba(194,172,238,.55)', 0.85);
  }
}

/* ---------- 演示模式：按真实调度语义仿真候选流 ---------- */
const DEMO_NAMES = ['MgB2', 'LaH10', 'H3S', 'YB6', 'CaH6', 'Li2B', 'ScH3',
  'YH6', 'BaH12', 'AlH3', 'NaB3', 'KH5', 'CaB6', 'Mg2C', 'SrH4', 'BH',
  'Li3N', 'C4S', 'TiH2', 'ZrB2', 'HfC', 'WN2', 'SiO2', 'FeB4'];
const DEMO_REJECT_P = [0.30, 0.16, 0.22, 0.10, 0.14, 0.14, 0.18, 0.22, 0.10];
const DEMO_LABELS = ['PROPHET_UNSTABLE', 'HULL_REJECT', 'QE_PHONON_UNSTABLE',
  'GAP_REJECT', 'MAGNETIC_REJECT', 'PROPHET_SOFT_MODE_SUSPECT', 'PM_REJECT'];
const DEMO_METRICS = {
  S1_symmetry: {spacegroup_after: 'P6/mmm', spacegroup_after_number: 191},
  S2_alignn_gap: {gap_ev: 0.42},
  S3_vasp_relax: {final_energy_ev: -18.3312},
  S4_vasp_magnetic: {delta_e_mag_mev_per_atom: 0.31},
  S5_mp_hull: {energy_above_hull_ev_per_atom: 0.0091},
  S6_prophet_phonons: {minimum_frequency_thz: 1.2 / THZ_TO_CM1},
  S7_qe_relax_pm: {P_over_cell_mass: 0.71},
  S8_qe_phonons: {global_minimum_cm1: 34.2},
  S9_epw_tc: {tc: {tc_primary_K: 0, lambda_epw: 0.82, omega_log_mev: 310}},
};

function useDemoCands(on) {
  const [cands, setCands] = useState([]);
  const seq = useRef(0);
  useEffect(() => {
    if (!on) { setCands([]); return; }
    const init = [];
    for (let i = 0; i < 26; i++) {
      const inPool = i >= 10;
      init.push({
        id: `d${seq.current++}`,
        formula: DEMO_NAMES[i % DEMO_NAMES.length] +
          (i >= DEMO_NAMES.length ? '*' : ''),
        material_id: `DEMO-${1000 + i}`,
        status: inPool ? 'pending' : 'active',
        current_stage: inPool ? '' : STAGES[i % 3],
      });
    }
    setCands(init);
    const t = setInterval(() => setCands(prev => {
      const out = [];
      for (const c of prev) {
        if (c.status === 'pending' || c.status === 'done' ||
            c.status === 'rejected') { out.push(c); continue; }
        const si = stageIndexOf(c.current_stage);
        if (Math.random() < DEMO_REJECT_P[Math.max(0, si)]) {
          out.push({...c, status: 'rejected',
                    current_stage: STAGES[si],
                    terminal_label: DEMO_LABELS[
                      Math.floor(Math.random() * DEMO_LABELS.length)]});
        } else if (si >= STAGES.length - 1) {
          const tcK = 5 + Math.random() * 40;
          const m = {...DEMO_METRICS.S9_epw_tc,
                     tc: {tc_K: tcK,
                          tc_primary_K: tcK,
                          lambda_epw: 0.6 + Math.random() * 0.6,
                          omega_log_mev: 250 + Math.random() * 200}};
          out.push({...c, status: 'done',
                    terminal_label: 'TC_REVIEW_COMPLETE', tc: m.tc,
                    metrics: m});
        } else {
          const ns = STAGES[si + 1];
          out.push({...c, current_stage: ns, metrics: DEMO_METRICS[ns] || null});
        }
      }
      // 池子补位
      const activeN = out.filter(c => c.status === 'active').length;
      if (activeN < 12) {
        const pool = out.find(c => c.status === 'pending');
        if (pool) {
          pool.status = 'active';
          pool.current_stage = STAGES[0];
        }
      }
      while (out.filter(c => c.status === 'pending').length < 8 &&
             seq.current < 400) {
        out.push({
          id: `d${seq.current++}`,
          formula: DEMO_NAMES[seq.current % DEMO_NAMES.length] + '*',
          material_id: `DEMO-${1000 + seq.current}`,
          status: 'pending', current_stage: '',
        });
      }
      // 终态只保留最近 24 条，防止数组无限涨
      const keep = out.filter(c => c.status !== 'rejected' && c.status !== 'done');
      const term = out.filter(c => c.status === 'rejected' || c.status === 'done')
        .slice(-24);
      return [...keep, ...term];
    }), 1300);
    return () => clearInterval(t);
  }, [on]);
  return cands;
}

/* ---------- 页面 ---------- */
export default function Showcase() {
  // URL 带 ?demo=1 直接进演示模式（给老板演示用）
  const [demo, setDemo] = useState(
    () => new URLSearchParams(window.location.search).has('demo'));
  const [modal, setModal] = useState(null);
  const {data: cands} = usePolling('/candidates', {limit: 400}, 6000);
  const {data: activeData} = usePolling('/candidates',
    {status: 'active', limit: 80}, 6000);
  const {data: passedData} = usePolling('/candidates',
    {status: 'passed', limit: 80}, 15000);
  const {data: winnersData} = usePolling('/candidates',
    {status: 'done', limit: 20, ordering: 'tc'}, 15000);
  const {data: rejectData} = usePolling('/candidates',
    {status: 'rejected', limit: 40}, 5000);
  const {data: ov} = usePolling('/overview', null, 6000);
  const {data: events} = usePolling('/events', {limit: 20}, 5000);
  const {data: cluster} = usePolling('/cluster_status', null, 8000);
  // 候选池样本：散点星图展示待入环候选（总数走 overview 聚合）
  const {data: pendingData} = usePolling('/candidates',
    {status: 'pending', limit: 1500}, 10000);
  // 真实 job 生命周期：上机 / 完成 / 失败都带真实时间戳，是页面上
  // 变化最快的一手真数据（任务池全天都有 job 在提交/跑完）
  const {data: jobsData} = usePolling('/jobs', {limit: 60}, 6000);

  const demoCands = useDemoCands(demo);
  const merged = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const c of [...(demo ? demoCands : []), ...(activeData || []),
                     ...(passedData || []), ...(cands || [])]) {
      if (seen.has(c.id)) continue;
      seen.add(c.id);
      out.push(c);
    }
    return out;
  }, [cands, activeData, passedData, demoCands, demo]);

  /* ---- 动画引擎：orbit 结构 + 粒子，全部走 ref，rAF 直绘 ---- */
  const orbitRef = useRef(new Map());   // id → {si, targetSi, state, born, ...}
  const particlesRef = useRef([]);      // 飞行粒子（含文字）
  const fxRef = useRef([]);             // 冲击波 / 螺旋入场等特效
  const hitRef = useRef([]);            // 帧内标记位置（点击拾取）
  const metricsCacheRef = useRef({});   // `${id}:${stage}` → 文本
  const pendingRef = useRef([]);        // 候选池样本（draw 循环读取）
  const poolPosRef = useRef(new Map()); // id → 池散点位置（入环飞行起点）
  const poolTotalRef = useRef(0);       // 候选池总数（POOL 标签用）
  const scatterRef = useRef({key: '', off: null, hits: [], tw: []});
  const scatterHitsRef = useRef([]);    // 散点点击拾取（持久，不逐帧重建）
  const joinedIdsRef = useRef(new Set()); // 已入环候选（星位熄灭）
  const scatterVersionRef = useRef(0);  // 散点场重建版本号（入环即重建）
  const jobsSeenRef = useRef(new Map());  // job id → 上次状态（生命周期 diff）
  const jobsRunRef = useRef(new Map());   // candidate id → 起跑时刻 ms（真实计时器）
  const firstJobsRef = useRef(true);
  const mouseRef = useRef(null);        // 画布内鼠标位置（悬停显示化学式）
  const canvasRef = useRef(null);
  const sizeRef = useRef({w: 900, h: 600, dpr: 1});
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  // 池样本/总数喂给 draw 循环（演示模式用仿真 pending）
  const poolTotal = (ov?.candidates_by_status_all || {}).pending || 0;
  useEffect(() => {
    pendingRef.current = demo
      ? demoCands.filter(c => c.status === 'pending').slice(0, 96)
      : (pendingData || []);
    poolTotalRef.current = demo
      ? pendingRef.current.length
      : (poolTotal || pendingRef.current.length);
  }, [pendingData, demo, demoCands, poolTotal]);

  const spawnFly = useCallback((x, y, text, color, opts = {}) => {
    if (!text) return;
    const ang = opts.ang != null ? opts.ang : Math.random() * Math.PI * 2;
    const sp = 70 + Math.random() * 90;
    particlesRef.current.push({
      kind: 'fly', x, y, vx: Math.cos(ang) * sp, vy: Math.sin(ang) * sp,
      life: 0, ttl: opts.ttl || 2.6, text, color: color || '#8fd8ff',
      trail: [], size: opts.size || 1,
    });
  }, []);

  /* stage 推进 → 立即飞一行关卡名；再异步取 detail 把真实指标补飞一行 */
  const onAdvance = useCallback((p, newStage, c) => {
    const {w, h} = sizeRef.current;
    const ang = p.ang != null ? p.ang : Math.random() * Math.PI * 2;
    const pos = ringXY(ang, w, h, animTs(performance.now()));
    const x = pos.x, y = pos.y;
    spawnFly(x, y, `${p.formula} ✓ ${STAGE_HUMAN[newStage] || newStage}`,
             '#9fe8ff', {ang, ttl: 2.2});
    const key = `${c.id}:${newStage}`;
    if (metricsCacheRef.current[key]) {
      spawnFly(x + 6, y + 10, metricsCacheRef.current[key], '#7fe0b0',
               {ang: ang + 0.15, ttl: 3.0});
      return;
    }
    if (demo) {  // 演示数据自带 metrics
      const t = metricText(newStage, c.metrics);
      if (t) metricsCacheRef.current[key] = t;
      spawnFly(x + 6, y + 10, t, '#7fe0b0', {ang: ang + 0.15, ttl: 3.0});
      return;
    }
    apiGet(`/candidates/${c.id}`)
      .then(d => {
        const rs = (d.stage_results || [])
          .filter(r => stageIndexOf(r.stage) === stageIndexOf(newStage));
        const r = rs[rs.length - 1];
        const t = r ? metricText(newStage, r.metrics) : null;
        // 该候选历史关卡的指标一并缓存（气氛脉搏免请求）
        for (const rr of (d.stage_results || [])) {
          const st = STAGES[stageIndexOf(rr.stage)];
          const tt = st ? metricText(st, rr.metrics) : null;
          if (tt) metricsCacheRef.current[`${c.id}:${st}`] = tt;
        }
        if (!t) return;
        metricsCacheRef.current[key] = t;
        // 结构可能已随轮转移动：按当前角度重算发射点
        const ang2 = p.ang != null ? p.ang : ang;
        const pos2 = ringXY(ang2, w, h, animTs(performance.now()));
        spawnFly(pos2.x + 6, pos2.y + 10,
                 t, '#7fe0b0', {ang: ang2 + 0.15, ttl: 3.0});
      })
      .catch(() => {});
  }, [demo, spawnFly]);

  /* 不稳定退场：参考组件的红色外飞 + 拖尾 + 扩散环 + 火星，同时飞原因文字 */
  const onReject = useCallback((p, c) => {
    const {w, h} = sizeRef.current;
    const ts = animTs(performance.now());
    const ang = p.ang != null ? p.ang : Math.random() * Math.PI * 2;
    const pos = ringXY(ang, w, h, ts);
    const out = ringXY(ang, w, h, ts, 1.04);
    const inn = ringXY(ang, w, h, ts, 0.96);
    let nx = out.x - inn.x, ny = out.y - inn.y;
    const nl = Math.hypot(nx, ny) || 1;
    const reason = labelHuman(c.terminal_label) || '淘汰';
    fxRef.current.push({
      kind: 'reject', x: pos.x, y: pos.y, nx: nx / nl, ny: ny / nl,
      life: 0, ttl: 2.0, color: '237,132,145',
    });
    spawnFly(pos.x, pos.y, `${p.formula} ✕ ${reason}`, '#ff938c',
             {ang: ang + (Math.random() - 0.5), ttl: 3.2, size: 1.2});
  }, [spawnFly]);

  /* 稳定入库：螺旋收进核心 + 金环冲击波 + Tc 升起 */
  const onVault = useCallback((p, c) => {
    const {w, h} = sizeRef.current;
    const cx = w / 2, cy = h * 0.46;
    const ang = p.ang != null ? p.ang : Math.random() * Math.PI * 2;
    const pos = ringXY(ang, w, h, animTs(performance.now()));
    const tcK = c.tc && c.tc.tc_K != null ? Number(c.tc.tc_K) : null;
    fxRef.current.push({
      kind: 'vault', x: pos.x, y: pos.y, ang,
      life: 0, ttl: 2.4, formula: p.formula, tcK,
      color: '242,211,147',
    });
    const wave = () => {
      fxRef.current.push(
        {kind: 'shock', x: cx, y: cy, life: 0, ttl: 1.1, color: '255,209,35'});
      // 参考组件：入库完成时核心一圈柔和的淡紫爆发
      fxRef.current.push(
        {kind: 'burst', x: cx, y: cy, life: 0, ttl: 0.9,
         color: '216,207,249'});
    };
    setTimeout(wave, 2400);
    if (tcK != null)
      setTimeout(() => spawnFly(cx, cy - 40,
        `${p.formula} Tc ≈ ${tcK.toFixed(2)} K 已入库 ★`, '#ffd123',
        {ang: -Math.PI / 2, ttl: 3.4, size: 1.4}), 2480);
  }, [spawnFly]);

  /* ---- 气氛脉搏（仅演示模式）：demo 调度全速跑，定时补飞指标 ----
     真实模式不重播旧指标，只播真事件（job 生命周期 / stage 推进）。 */
  const ambientBusyRef = useRef(0);   // detail 请求限流（≥1.5s 一次）
  useEffect(() => {
    const timer = setInterval(() => {
      if (!demo) return;   // 真实模式不重播旧指标，只播真事件（见 job 生命周期）
      const ps = orbitRef.current
        ? [...orbitRef.current.values()].filter(p => p.state === 'running')
        : [];
      if (!ps.length) return;
      const p = ps[Math.floor(Math.random() * ps.length)];
      const stage = STAGES[Math.round(p.targetSi)];
      if (!stage) return;
      const key = `${p.id}:${stage}`;
      const {w, h} = sizeRef.current;
      const emit = t => {
        const ang = p.ang != null ? p.ang : Math.random() * Math.PI * 2;
        const pos = ringXY(ang, w, h, animTs(performance.now()));
        spawnFly(pos.x + 6, pos.y + 10,
                 t, '#7fe0b0', {ang: ang + 0.12, ttl: 2.8});
      };
      if (metricsCacheRef.current[key]) {
        emit(metricsCacheRef.current[key]);
        return;
      }
      if (demo) {
        const c = demoCands.find(x => String(x.id) === p.id);
        const t = c ? metricText(stage, c.metrics) : null;
        if (t) { metricsCacheRef.current[key] = t; emit(t); }
        return;
      }
      const now = Date.now();
      if (now - ambientBusyRef.current < 1500) return;
      ambientBusyRef.current = now;
      // 一次 detail 把该候选所有关卡的指标都缓存，后续脉搏零请求
      apiGet(`/candidates/${p.realId}`)
        .then(d => {
          for (const r of (d.stage_results || [])) {
            const st = STAGES[stageIndexOf(r.stage)];
            if (!st) continue;
            const t = metricText(st, r.metrics);
            if (t) metricsCacheRef.current[`${p.id}:${st}`] = t;
          }
          if (metricsCacheRef.current[key])
            emit(metricsCacheRef.current[key]);
        })
        .catch(() => {});
    }, 2300);
    return () => clearInterval(timer);
  }, [demo, demoCands, spawnFly]);

  /* ---- 轮询数据 diff：驱动一切动画事件 ---- */
  useEffect(() => {
    const orbit = orbitRef.current;
    for (const c of merged) {
      const id = String(c.id);
      const p = orbit.get(id);
      // 终态：在环 → 触发退场/入库动画并移出；不在环 → 忽略
      //（窗口外的零散终态由下面的终态监视器兜底）
      if (c.status === 'done') {
        if (p && !seenDoneRef.current.has(id)) {
          seenDoneRef.current.add(id);
          orbit.delete(id);
          onVault(p, c);
        }
        continue;
      }
      if (c.status === 'rejected' || c.status === 'quarantine') {
        if (p) {
          orbit.delete(id);
          onReject(p, c);
        }
        continue;
      }
      if (c.status !== 'active' && c.status !== 'passed') continue;
      const si = stageIndexOf(c.current_stage);
      if (!p) {
        const h = [...id].reduce((a, ch) => a + ch.charCodeAt(0), 0);
        const np = {id, realId: c.id,
          formula: c.formula || c.material_id || `#${c.ordinal}`,
          targetSi: si,
          state: c.job_status === 'running' || c.job_status === 'dispatched'
            ? 'running' : c.job_status === 'queued' ? 'queued' : 'passed',
          born: performance.now(), jitter: (h % 97) / 97, ang: null};
        // 候选在星散场里有"星位" → 播放"入环"飞行（星位 → 环上槽位）
        const pp = poolPosRef.current.get(id);
        if (pp) {
          np.join = {x0: pp.x, y0: pp.y, t0: performance.now()};
          joinedIdsRef.current.add(id);        // 星位实时熄灭
          scatterVersionRef.current++;
        }
        orbit.set(id, np);
      } else {
        if (si >= 0 && si !== p.targetSi) {
          p.targetSi = si;
          onAdvance(p, STAGES[si] || c.current_stage, c);
        }
        p.state = c.job_status === 'running' || c.job_status === 'dispatched'
          ? 'running' : c.job_status === 'queued' ? 'queued' : 'passed';
      }
    }
    // 从窗口里彻底消失的在环结构（大批投喂挤出窗口）：清掉即可，
    // 它的终态事件由终态监视器按关卡位置补发
    for (const id of [...orbit.keys()])
      if (!merged.some(c => String(c.id) === id)) orbit.delete(id);
  }, [merged, onAdvance, onReject, onVault]);

  /* 终态监视器：轮询窗口外的淘汰/入库也能触发动画（首轮只建索引不炸历史） */
  const seenDoneRef = useRef(new Set());
  const seenRejectRef = useRef(new Set());
  const firstTermRef = useRef(true);
  useEffect(() => {
    if (firstTermRef.current) {
      if (rejectData && winnersData) {
        for (const c of rejectData) seenRejectRef.current.add(`r${c.id}`);
        for (const c of winnersData) seenDoneRef.current.add(String(c.id));
        firstTermRef.current = false;
      }
      return;
    }
    for (const c of (rejectData || [])) {
      const id = `r${c.id}`;
      if (seenRejectRef.current.has(id)) continue;
      seenRejectRef.current.add(id);
      const si = stageIndexOf(c.current_stage);
      if (si < 0) continue;
      const ang = (si + 0.5) / STAGES.length * Math.PI * 2;
      onReject({ang, formula: c.formula || c.material_id || `#${c.id}`}, c);
    }
    for (const c of (winnersData || [])) {
      const id = String(c.id);
      if (seenDoneRef.current.has(id)) continue;
      seenDoneRef.current.add(id);
      onVault({ang: Math.random() * Math.PI * 2,
               formula: c.formula || c.material_id || `#${c.id}`},
              {tc: c.tc || null});
    }
  }, [rejectData, winnersData, onReject, onVault]);

  /* ---- 真实 job 生命周期 → 动画（全是 Job 表的一手真事件）----
     上机 / 完成 / 失败各带真实时间戳：完成与失败飞真实用时与真实
     error_class；running 的结构点挂秒级计时器（每帧真实走秒）。 */
  useEffect(() => {
    if (demo || !jobsData) return;
    const seen = jobsSeenRef.current;
    const runMap = jobsRunRef.current;
    if (firstJobsRef.current) {
      for (const j of jobsData) {
        if (!j.candidate_id) continue;
        seen.set(j.id, j.status);
        if (j.status === 'running')
          runMap.set(String(j.candidate_id),
                     Date.parse(j.started_at || j.submitted_at ||
                                new Date().toISOString()));
      }
      firstJobsRef.current = false;
      return;
    }
    const flyAt = (p, text, color) => {
      const {w, h} = sizeRef.current;
      const ang = p.ang != null ? p.ang : Math.random() * Math.PI * 2;
      const pos = ringXY(ang, w, h, animTs(performance.now()));
      spawnFly(pos.x + 6, pos.y + 10,
               text, color, {ang: ang + 0.12, ttl: 3.0});
    };
    for (const j of jobsData) {
      if (!j.candidate_id) continue;        // mgmt job 不上星环
      const prev = seen.get(j.id);
      seen.set(j.id, j.status);
      if (prev === j.status) continue;
      const cid = String(j.candidate_id);
      const t0 = Date.parse(j.started_at || j.submitted_at ||
                            new Date().toISOString());
      const si = stageIndexOf(j.stage);
      const sName = si >= 0 ? STAGE_SHORT[si]
        : (j.stage || '').replace(/^S\d+_/, '').slice(0, 8);
      if (j.status === 'running') {
        runMap.set(cid, t0);
        const p = orbitRef.current.get(cid);
        if (p) flyAt(p, `${p.formula} ▸ ${sName} 已上机`, '#8fd8ff');
      } else if (j.status === 'succeeded') {
        runMap.delete(cid);
        const p = orbitRef.current.get(cid);
        if (p) {
          const dur = Math.max(0, Date.parse(j.finished_at ||
                                             new Date().toISOString()) - t0);
          const durTxt = dur >= 5400000
            ? `${(dur / 3600000).toFixed(1)} h`
            : dur >= 60000 ? `${Math.round(dur / 60000)} min`
              : `${Math.round(dur / 1000)} s`;
          flyAt(p, `${p.formula} ✓ ${sName} 完成 · ${durTxt}`, '#7fe0b0');
        }
      } else if (j.status === 'failed' || j.status === 'timeout') {
        runMap.delete(cid);
        const p = orbitRef.current.get(cid);
        if (p) flyAt(p, `${p.formula} ✕ ${sName} 失败` +
                     (j.error_class ? ` ${j.error_class}` : ''), '#ff938c');
      } else if (j.status === 'cancelled' || j.status === 'lost') {
        runMap.delete(cid);
        const p = orbitRef.current.get(cid);
        if (p) flyAt(p, `${p.formula} ◦ ${sName} ${j.status}`, 'rgba(125,139,153,0.9)');
      }
    }
    // seen 表防膨胀：只清理已滑出 60 条窗口的旧 job
    if (seen.size > 500) {
      const live = new Set(jobsData.map(j => j.id));
      for (const k of [...seen.keys()]) {
        if (seen.size <= 400) break;
        if (!live.has(k)) seen.delete(k);
      }
    }
  }, [jobsData, demo, spawnFly]);

  /* ---- 画布主循环 ---- */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let raf, rot = 0, lastT = performance.now();

    const ro = new ResizeObserver(() => {
      const el = canvas.parentElement;
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      sizeRef.current = {w: el.clientWidth, h: el.clientHeight, dpr};
      canvas.width = el.clientWidth * dpr;
      canvas.height = el.clientHeight * dpr;
      canvas.style.width = `${el.clientWidth}px`;
      canvas.style.height = `${el.clientHeight}px`;
    });
    ro.observe(canvas.parentElement);

    const ARC_GAP = 0.055;                       // 弧段间隙（rad）
    const span = Math.PI * 2 / STAGES.length;
    const arcA0 = i => STAGE_START + rot + i * span + ARC_GAP / 2;
    const arcA1 = i => STAGE_START + rot + (i + 1) * span - ARC_GAP / 2;

    const draw = (t) => {
      raf = requestAnimationFrame(draw);
      const dt = Math.min(0.05, (t - lastT) / 1000);
      lastT = t;
      // 参考组件的关卡标签固定不动：舞台不自转，动感来自环带/球面/轨道
      // 和结构在环上行走；rot 恒为 0，结构角度靠 p.ang 缓动追槽位

      const {w, h, dpr} = sizeRef.current;
      const cx = w / 2, cy = h * 0.46;
      const R = Math.min(w, h) * 0.28;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);

      // 背景网格 + 中心辉光
      ctx.strokeStyle = 'rgba(125,139,153,0.06)';
      ctx.lineWidth = 1;
      for (let x = 0; x < w; x += 48) {
        ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, h); ctx.stroke();
      }
      for (let y = 0; y < h; y += 48) {
        ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(w, y); ctx.stroke();
      }
      const glow = ctx.createRadialGradient(cx, cy, 0, cx, cy, R * 1.15);
      glow.addColorStop(0, 'rgba(255,209,35,0.05)');
      glow.addColorStop(0.45, 'rgba(76,141,255,0.05)');
      glow.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = glow;
      ctx.fillRect(0, 0, w, h);

      // 每关在环数量（活跃弧段高亮）+ 弧段内均匀占位。
      // 结构按目标关分组后在本关弧段内等距排开，随机抖动只留极小摆动，
      // 避免同关光点挤在一起导致公式文字互相压盖。
      const orbit = orbitRef.current;
      const byStage = Array(STAGES.length).fill(0);
      const stageLists = Array.from({length: STAGES.length}, () => []);
      for (const p of orbit.values()) {
        const si = Math.max(0, Math.min(STAGES.length - 1,
          Math.round(p.targetSi)));
        byStage[si]++;
        stageLists[si].push(p);
      }
      const MAX_PER_ARC = 12;
      for (let i = 0; i < STAGES.length; i++) {
        const arr = stageLists[i];
        // 单关超过上限时：在跑的必显示，其余新的优先；总数走 ×N 计数
        arr.sort((a, b) =>
          ((a.state === 'running' ? 0 : 1) - (b.state === 'running' ? 0 : 1)) ||
          (b.born - a.born));
        const shown = arr.slice(0, MAX_PER_ARC);
        for (const p of arr) p.shown = false;
        const usable = span * 0.72;
        shown.forEach((p, idx) => {
          p.shown = true;
          const frac = shown.length === 1 ? 0.5 : (idx + 0.5) / shown.length;
          const wobble = Math.sin(t / 900 + p.jitter * 9) * 0.012;
          const targetAng = STAGE_START + rot + i * span + span / 2 +
            (frac - 0.5) * usable + wobble;
          // 结构不瞬移：沿环缓动走到新关卡槽位（参考组件的 gate travel）
          if (p.ang == null) p.ang = targetAng;
          else {
            const dAng = Math.atan2(Math.sin(targetAng - p.ang),
                                    Math.cos(targetAng - p.ang));
            p.ang += dAng * Math.min(1, dt * 1.6);
          }
          p.targetAng = targetAng;
          p.labelBelow = idx % 2 === 1;   // 标签上下交错，减少文字重叠
        });
      }

      // ---- 中心星环（参考 BCS 星环组件：倾斜 3D 编织环 + 球面 + 开放 CORE）----
      const ts = animTs(t);
      const proj = makeProject(w, h, ts);
      const rRing = R * 0.16;
      drawSphere(ctx, proj, R, ts);
      drawBraidedRing(ctx, proj, R, rRing, ts);
      drawCometOrbits(ctx, proj, R, ts);
      drawTickRing(ctx, proj, R);

      // 9 段关卡白色短弧（参考组件：0.38rad 弧段，不是整关长弧）
      for (let i = 0; i < STAGES.length; i++) {
        const a0 = arcA0(i), a1 = arcA1(i);
        const am = (a0 + a1) / 2;
        const hot = byStage[i] > 0;
        for (let layer = 0; layer < 3; layer++) {
          const rad = R * (1.08 + layer * 0.012), pts = [];
          for (let j = 0; j <= 24; j++) {
            const a = am - 0.19 + 0.38 * j / 24;
            pts.push(proj(Math.cos(a) * rad, Math.sin(a) * rad, rRing * 0.8));
          }
          strokeLine(ctx, pts, hot && layer === 0
            ? 'rgba(221,200,255,.95)'
            : layer === 0 ? 'rgba(219,221,239,.58)'
              : 'rgba(162,144,211,.18)',
          hot && layer === 0 ? 2 : layer === 0 ? 1 : 0.5);
        }
        if (hot) glowDot(ctx, proj(Math.cos(am) * R, Math.sin(am) * R, 0));
        // 关卡标签：投影到 1.49R，夹进画布并避开两侧 HUD 面板
        const endpoint = ringXY(am, w, h, ts, 1.49);
        let lx = Math.max(48, Math.min(w - 48, endpoint.x));
        let ly = Math.max(27, Math.min(h - 58, endpoint.y));
        if (ly < h * 0.55 && lx < 310) lx = 310;
        if (ly < h * 0.55 && lx > w - 335) lx = w - 335;
        if (ly > h - 250 && lx < 300) lx = 300;
        const connector = ringXY(am, w, h, ts, 1.30);
        const onRing = ringXY(am, w, h, ts, 1.0, rRing);
        strokeLine(ctx, [onRing, connector], hot
          ? 'rgba(193,169,244,.7)' : 'rgba(170,184,210,.2)', 0.6);
        const stageName = STAGE_SHORT[i].replace(/^S\d+\s*/, '');
        ctx.textAlign = 'center';
        ctx.font = '8px "SF Mono", Consolas, monospace';
        ctx.fillStyle = hot ? 'rgba(193,169,244,.8)'
          : 'rgba(125,139,153,.45)';
        ctx.fillText(`S${String(i + 1).padStart(2, '0')}`, lx, ly - 8);
        ctx.font = '10.5px "SF Mono", Consolas, monospace';
        ctx.fillStyle = hot ? 'rgba(225,214,255,.95)'
          : 'rgba(125,139,153,.65)';
        ctx.fillText(stageName, lx, ly + 3);
        if (byStage[i] > 0) {
          ctx.fillStyle = 'rgba(193,169,244,.9)';
          ctx.font = '9.5px "SF Mono", Consolas, monospace';
          ctx.fillText(`×${byStage[i]}`, lx, ly + 15);
        }
      }

      // 开放 CORE 曲面网格 + 金色入库标签（语义保留）
      drawCoreSurface(ctx, proj, R, ts);
      const coreC = proj(0, 0, 0);
      ctx.fillStyle = 'rgba(255,224,110,.95)';
      ctx.font = '11px "SF Mono", Consolas, monospace';
      ctx.textAlign = 'center';
      ctx.fillText('CORE', coreC.x, coreC.y + 4);
      ctx.fillStyle = 'rgba(255,209,35,.68)';
      ctx.fillText('入库', coreC.x, coreC.y + 18);

      // 结构标记
      hitRef.current = [];
      for (const p of orbit.values()) {
        if (!p.shown) continue;         // 超上限的结构不上环（计数已表达）
        const mp = proj(Math.cos(p.ang) * R, Math.sin(p.ang) * R, 0);
        const x = mp.x, y = mp.y;
        // 入环：从候选池星位沿外弧飞入环上槽位（参考组件的 entering）
        if (p.join) {
          const DUR = 2800;
          const u = (t - p.join.t0) / DUR;
          if (u >= 1) {
            fxRef.current.push({kind: 'shock', x, y, life: 0, ttl: 0.7,
                                color: '111,168,255'});
            delete p.join;
          } else {
            const ent = k => {
              const uk = Math.max(0, u - k * 0.026);
              const vk = 1 - Math.pow(1 - uk, 3);
              const a = p.ang - 0.55 * (1 - vk);
              const rad = R * (1.9 - 0.9 * vk);
              const q = proj(Math.cos(a) * rad, Math.sin(a) * rad,
                             Math.sin(uk * Math.PI) * R * 0.33);
              // 起点从候选池星位淡出到环上入射点
              const wgt = Math.pow(1 - vk, 2);
              return {x: q.x + (p.join.x0 - q.x) * wgt,
                      y: q.y + (p.join.y0 - q.y) * wgt};
            };
            const trail = [];
            for (let k = 0; k <= 9; k++) trail.push(ent(k));
            strokeLine(ctx, trail, 'rgba(111,168,255,0.5)', 1.3);
            glowMarker(ctx, trail[0], '111,168,255', 3.2, 0.95);
            continue;                    // 环上正式标记暂不画，避免双影
          }
        }
        hitRef.current.push({x, y, p});
        const running = p.state === 'running';
        const moving = p.targetAng != null &&
          Math.abs(Math.atan2(Math.sin(p.targetAng - p.ang),
                              Math.cos(p.targetAng - p.ang))) > 0.03;
        const col = p.state === 'queued' ? '125,139,153'
          : running ? '111,168,255' : '127,224,176';
        // 在环上行走 / 在算的结构带短彗尾（参考组件的 screening trails）
        if (running || moving)
          drawRingTrail(ctx, proj, p.ang, R, col, running ? 0.5 : 0.3);
        // 轨道小连线（结构 → 核心，细线增强"向心"感）
        ctx.beginPath();
        ctx.moveTo(x, y);
        const cp = proj(Math.cos(p.ang) * R * 0.45,
                        Math.sin(p.ang) * R * 0.45, 0);
        ctx.lineTo(cp.x, cp.y);
        ctx.strokeStyle = 'rgba(125,139,153,0.07)';
        ctx.lineWidth = 1;
        ctx.stroke();
        const pulse = running ? 3.4 + Math.sin(t / 180 + p.jitter * 7) * 1.2
          : 3.0;
        glowMarker(ctx, {x, y}, col, pulse, running ? 0.95 : 0.62);
        // 公式标签：沿环法线内外交错，避免相邻文字压盖
        const labR = R + (p.labelBelow ? 14 : -13);
        const lp = proj(Math.cos(p.ang) * labR, Math.sin(p.ang) * labR, 0);
        const lx = lp.x, ly = lp.y;
        ctx.fillStyle = 'rgba(215,222,230,0.92)';
        ctx.font = '11px "SF Mono", Consolas, monospace';
        ctx.textAlign = 'center';
        ctx.fillText(p.formula, lx, ly + 4);
        // 真实任务秒级计时：running job 的 started_at 每帧真实走秒
        const t0 = jobsRunRef.current.get(String(p.realId));
        if (p.state === 'running' && t0) {
          const el = Math.max(0, t - t0) / 1000;
          const hh = String(Math.floor(el / 3600)).padStart(2, '0');
          const mm = String(Math.floor(el / 60) % 60).padStart(2, '0');
          const ss = String(Math.floor(el) % 60).padStart(2, '0');
          ctx.fillStyle = 'rgba(111,168,255,0.8)';
          ctx.font = '9.5px "SF Mono", Consolas, monospace';
          ctx.textAlign = 'center';
          ctx.fillText(`⏱ ${hh}:${mm}:${ss}`,
                       lx, p.labelBelow ? ly + 16 : ly - 9);
        }
      }

      // 候选池星散场：全部待入环候选按 id 确定性散落在画布空白区
      // （避开主环 + 两侧 HUD 面板），静态点预渲染到离屏 canvas，
      // 每帧只 drawImage，点再多也不拖帧。悬停显示化学式；
      // 候选被调度激活时从其"星位"飞进主环。
      const pool = pendingRef.current;
      const scatterKey = `${w}x${h}:${pool.length}:` +
        `${pool[0] ? pool[0].id : ''}:${pool[pool.length - 1]
          ? pool[pool.length - 1].id : ''}:${scatterVersionRef.current}`;
      if (scatterRef.current.key !== scatterKey) {
        const off = document.createElement('canvas');
        off.width = w * dpr;
        off.height = h * dpr;
        const c2 = off.getContext('2d');
        c2.setTransform(dpr, 0, 0, dpr, 0, 0);
        const hits = [];
        const tw = [];                       // 闪烁星（亮点点位 + 相位）
        const keepR = R * 1.5 + 24;          // 主环 + 刻度/标签带之外的禁区
        for (const c of pool) {
          if (joinedIdsRef.current.has(String(c.id))) continue;  // 已入环
          let seed = 0;
          for (const ch of String(c.id)) seed = seed * 31 + ch.charCodeAt(0)
            | 0;
          const rnd = mulberry32(seed);
          let x = 0, y = 0, ok = false;
          for (let tries = 0; tries < 24 && !ok; tries++) {
            x = 10 + rnd() * (w - 20);
            y = 10 + rnd() * (h - 20);
            ok = Math.hypot(x - cx, y - cy) > keepR &&
                 !(x < 300 && y < h * 0.55) &&          // VAULT 面板区
                 !(x < 290 && y > h - 250) &&           // PIPELINE 关卡面板区
                 !(x > w - 345 && y < h * 0.55);        // LIVE STREAM 面板区
          }
          if (!ok) continue;
          const bright = rnd() < 0.14;
          c2.beginPath();
          c2.arc(x, y, bright ? 1.7 : 1.1, 0, Math.PI * 2);
          c2.fillStyle = bright ? 'rgba(143,216,255,0.40)'
            : 'rgba(125,139,153,0.34)';
          c2.fill();
          hits.push({x, y, c});
          poolPosRef.current.set(String(c.id), {x, y});
          if (bright) tw.push({x, y, phase: rnd() * Math.PI * 2,
                               speed: 0.9 + rnd() * 1.4});
        }
        scatterRef.current = {key: scatterKey, off, hits, tw};
      }
      scatterHitsRef.current = scatterRef.current.hits;
      if (scatterRef.current.off)
        ctx.drawImage(scatterRef.current.off, 0, 0, w, h);
      // 星光闪烁：亮星按各自相位/频率脉动（数量 ~样本 14%，开销可忽略）
      for (const s of scatterRef.current.tw) {
        const a = 0.10 + 0.55 * (0.5 + 0.5 * Math.sin(t / 650 * s.speed +
                                                    s.phase));
        ctx.beginPath();
        ctx.arc(s.x, s.y, 1.6, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(159,232,255,${a})`;
        ctx.fill();
      }
      if (poolPosRef.current.size > 8000) poolPosRef.current = new Map();
      // 悬停：离鼠标最近的散点显示化学式
      const mp = mouseRef.current;
      if (mp) {
        let hov = null, hd = 16;
        for (const s of scatterHitsRef.current) {
          const d = Math.hypot(s.x - mp.x, s.y - mp.y);
          if (d < hd) { hd = d; hov = s; }
        }
        if (hov) {
          ctx.beginPath();
          ctx.arc(hov.x, hov.y, 3.4, 0, Math.PI * 2);
          ctx.strokeStyle = 'rgba(143,216,255,0.9)';
          ctx.lineWidth = 1.2;
          ctx.stroke();
          ctx.fillStyle = 'rgba(159,232,255,0.95)';
          ctx.font = '11px "SF Mono", Consolas, monospace';
          ctx.textAlign = 'center';
          ctx.fillText(hov.c.formula || hov.c.material_id,
                       hov.x, hov.y - 9);
        }
      }
      // 顶部居中：候选池总量（金色高亮）
      {
        ctx.font = '10.5px "SF Mono", Consolas, monospace';
        const label = 'POOL · 候选池 ';
        const count = `×${poolTotalRef.current}`;
        const lw = ctx.measureText(label).width;
        ctx.textAlign = 'left';
        ctx.fillStyle = 'rgba(125,139,153,0.75)';
        ctx.fillText(label, cx - (lw + ctx.measureText(count).width) / 2, 16);
        ctx.fillStyle = 'rgba(255,209,35,0.9)';
        ctx.fillText(count, cx - (lw + ctx.measureText(count).width) / 2 + lw,
                     16);
      }

      // 常驻喷流：每个在跑结构按概率喷彗尾（chuachuachua）
      for (const p of orbit.values()) {
        if (p.state !== 'running') continue;
        if (Math.random() < dt * 1.2) {
          const a = p.ang + (Math.random() - 0.5) * 0.3;
          const cpos = proj(Math.cos(a) * R, Math.sin(a) * R, 0);
          // 沿投影环法线喷出，倾斜环上方向才不偏
          const outp = proj(Math.cos(a) * R * 1.03,
                            Math.sin(a) * R * 1.03, 0);
          const inp = proj(Math.cos(a) * R * 0.97,
                           Math.sin(a) * R * 0.97, 0);
          let nx = outp.x - inp.x, ny = outp.y - inp.y;
          const nl = Math.hypot(nx, ny) || 1;
          nx /= nl; ny /= nl;
          const jit = (Math.random() - 0.5) * 0.7;
          const dx = nx * Math.cos(jit) - ny * Math.sin(jit);
          const dy = nx * Math.sin(jit) + ny * Math.cos(jit);
          const sp = 120 + Math.random() * 150;
          particlesRef.current.push({
            kind: 'comet', x: cpos.x, y: cpos.y,
            vx: dx * sp, vy: dy * sp,
            life: 0, ttl: 0.9 + Math.random() * 0.7,
            color: Math.random() < 0.3 ? '159,232,255' : '111,168,255',
          });
        }
      }

      // 粒子更新 + 绘制
      const parts = particlesRef.current;
      for (let i = parts.length - 1; i >= 0; i--) {
        const q = parts[i];
        q.life += dt;
        if (q.life >= q.ttl) { parts.splice(i, 1); continue; }
        const k = q.life / q.ttl;
        if (q.kind === 'comet') {
          q.x += q.vx * dt; q.y += q.vy * dt;
          q.vx *= (1 - dt * 0.4); q.vy *= (1 - dt * 0.4);
          ctx.beginPath();
          ctx.moveTo(q.x, q.y);
          ctx.lineTo(q.x - q.vx * 0.09, q.y - q.vy * 0.09);
          ctx.strokeStyle = `rgba(${q.color},${1 - k})`;
          ctx.lineWidth = 1.6;
          ctx.stroke();
          ctx.beginPath();
          ctx.arc(q.x, q.y, 1.6, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(${q.color},${0.9 - k * 0.9})`;
          ctx.fill();
        } else {                                     // fly：带文字的指标彗星
          q.x += q.vx * dt; q.y += q.vy * dt;
          q.vx *= (1 - dt * 0.55); q.vy *= (1 - dt * 0.55);
          q.trail.push([q.x, q.y]);
          if (q.trail.length > 9) q.trail.shift();
          ctx.beginPath();
          ctx.moveTo(q.trail[0][0], q.trail[0][1]);
          for (const [tx, ty] of q.trail) ctx.lineTo(tx, ty);
          ctx.strokeStyle = q.color;
          ctx.globalAlpha = (1 - k) * 0.4;
          ctx.lineWidth = 1;
          ctx.stroke();
          ctx.globalAlpha = 1 - k;
          ctx.beginPath();
          ctx.arc(q.x, q.y, 2.2, 0, Math.PI * 2);
          ctx.fillStyle = q.color;
          ctx.fill();
          ctx.font = `${11.5 * (q.size || 1)}px "SF Mono", Consolas, monospace`;
          ctx.fillStyle = q.color;
          ctx.fillText(q.text, q.x + 7, q.y + 4);
          ctx.globalAlpha = 1;
        }
      }

      // 特效：冲击波 / 淘汰外飞 / 入库螺旋
      const fxs = fxRef.current;
      for (let i = fxs.length - 1; i >= 0; i--) {
        const f = fxs[i];
        f.life += dt;
        if (f.life >= f.ttl) { fxs.splice(i, 1); continue; }
        const k = f.life / f.ttl;
        if (f.kind === 'shock') {
          ctx.beginPath();
          ctx.arc(f.x, f.y, 8 + k * 90, 0, Math.PI * 2);
          ctx.strokeStyle = `rgba(${f.color},${(1 - k) * 0.8})`;
          ctx.lineWidth = 2.5 * (1 - k) + 0.5;
          ctx.stroke();
        } else if (f.kind === 'reject') {
          // 参考组件的淘汰：沿环法线外飞的红点 + 拖尾 + 扩散环 + 5 颗火星
          const travel = u => u * u * Math.min(w, h) * 0.28 * 1.1;
          const d = travel(k);
          const px = f.x + f.nx * d, py = f.y + f.ny * d;
          const trail = [];
          for (let s = 0; s <= 13; s++)
            trail.push({x: f.x + f.nx * travel(Math.max(0, k - s * 0.02)),
                        y: f.y + f.ny * travel(Math.max(0, k - s * 0.02))});
          strokeLine(ctx, trail, `rgba(${f.color},${(1 - k) * 0.55})`, 1.3);
          glowMarker(ctx, {x: px, y: py}, f.color, 2.6, 1 - k);
          ctx.strokeStyle = `rgba(${f.color},.8)`;
          ctx.lineWidth = 0.7;
          ctx.beginPath();
          ctx.arc(px, py, 7 + k * 10, 0, TAU);
          ctx.stroke();
          for (let j = 0; j < 5; j++) {
            const a = j / 5 * TAU;
            ctx.fillStyle = `rgb(${f.color})`;
            ctx.fillRect(px + Math.cos(a) * k * 22,
                         py + Math.sin(a) * k * 22, 1.4, 1.4);
          }
        } else if (f.kind === 'vault') {
          // 参考组件：沿投影环面内旋 0.6 圈 + 垂直弧，收进核心
          const ease = 1 - Math.pow(1 - k, 2.4);
          const aa = f.ang != null ? f.ang : 0;
          const trail = [];
          for (let s = 0; s <= 12; s++) {
            const kk = Math.max(0, ease - s * 0.03);
            trail.push(proj(
              Math.cos(aa + kk * TAU * 0.6) * R * (1 - kk),
              Math.sin(aa + kk * TAU * 0.6) * R * (1 - kk),
              Math.sin(kk * Math.PI) * R * 0.20));
          }
          strokeLine(ctx, trail, `rgba(${f.color},${(1 - k) * 0.75})`, 1.8);
          const head = trail[0];
          glowMarker(ctx, head, f.color, 4, 1 - k * 0.4);
          if (k < 0.5) {
            ctx.fillStyle = `rgba(255,224,110,${1 - k * 2})`;
            ctx.font = '11px "SF Mono", Consolas, monospace';
            ctx.fillText(f.formula, head.x + 8, head.y - 8);
          }
        } else if (f.kind === 'burst') {
          // 入库完成：核心柔和的淡紫径向爆发
          const rad = R * 0.4;
          const g = ctx.createRadialGradient(f.x, f.y, 0, f.x, f.y, rad);
          g.addColorStop(0, `rgba(${f.color},${(1 - k) * 0.2})`);
          g.addColorStop(1, `rgba(${f.color},0)`);
          ctx.fillStyle = g;
          ctx.fillRect(f.x - rad, f.y - rad, rad * 2, rad * 2);
        }
      }
    };
    raf = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, []);

  /* 点击拾取：最近的环上结构 → 3D 结构弹窗 */
  const onCanvasClick = e => {
    const rect = canvasRef.current.getBoundingClientRect();
    const mx = e.clientX - rect.left, my = e.clientY - rect.top;
    let best = null, bd = 18;
    for (const hpt of hitRef.current) {
      const d = Math.hypot(hpt.x - mx, hpt.y - my);
      if (d < bd) { bd = d; best = hpt; }
    }
    // 散点星图单独拾取（静态缓存，不逐帧进 hitRef）
    for (const s of scatterHitsRef.current) {
      const d = Math.hypot(s.x - mx, s.y - my);
      if (d < 14 && d < bd) { bd = d; best = {scatter: s.c}; }
    }
    if (!best) return;
    if (best.pool) {   // 候选池光点：也能看 3D 结构
      setModal({realId: best.pool.id,
                formula: best.pool.formula || best.pool.material_id,
                label: '候选池 · 等待入环'});
      return;
    }
    if (best.scatter) {
      setModal({realId: best.scatter.id,
                formula: best.scatter.formula || best.scatter.material_id,
                label: '候选池 · 等待入环'});
      return;
    }
    const p = best.p;
    const c = merged.find(x => String(x.id) === p.id) || {};
    setModal({realId: p.realId, formula: p.formula,
              label: STAGE_HUMAN[STAGES[Math.round(p.targetSi)]] ||
                p.targetSi, tc: c.tc || null});
  };

  /* ---- HUD 面板数据 ---- */
  const statusBy = ov?.candidates_by_status_all || {};
  const total = Object.values(statusBy).reduce((a, b) => a + b, 0);
  const runningN = (statusBy.active || 0) + (statusBy.passed || 0);
  const rejectAgg = useMemo(() => {
    const raw = ov?.rejected_by_stage_all || {};
    const out = {};
    for (const [k, v] of Object.entries(raw)) {
      const si = stageIndexOf(k);
      out[si >= 0 ? si : k] = (out[si >= 0 ? si : k] || 0) + v;
    }
    return out;
  }, [ov]);
  const vault = useMemo(() => (winnersData || []).map(c => ({
    id: c.id, formula: c.formula || c.material_id,
    tc: c.tc ? c.tc.tc_K : null,
    lam: c.tc ? c.tc.lambda : null,
  })), [winnersData]);
  const vaultIds = vault.map(v => v.id).join(',');
  const freshRef = useRef(new Set());
  const [freshIds, setFreshIds] = useState([]);
  useEffect(() => {
    const fresh = vault.filter(v => !freshRef.current.has(v.id) &&
                                    freshRef.current.size > 0);
    for (const v of vault) freshRef.current.add(v.id);
    if (fresh.length) {
      setFreshIds(fresh.map(v => v.id));
      const t = setTimeout(() => setFreshIds([]), 2400);
      return () => clearTimeout(t);
    }
  }, [vaultIds]);   // eslint-disable-line

  const feed = useMemo(() => {
    const today = new Date().toLocaleDateString('sv');
    const items = [];
    for (const e of (events || [])) {
      if ((e.message || '').startsWith('reconcile:')) continue;
      items.push({
        key: `e${e.id}`,
        ts: (e.created_at || '').slice(0, 19),
        level: e.level,
        text: e.message,
      });
    }
    for (const c of (rejectData || [])) {
      const lh = labelHuman(c.terminal_label);
      if (!lh) continue;
      items.push({key: `r${c.id}`, ts: (c.updated_at || '').slice(0, 19),
                  level: 'rejected',
                  text: `「${c.formula || c.material_id}」${lh}`});
    }
    for (const c of (winnersData || [])) {
      items.push({key: `w${c.id}`, ts: (c.updated_at || '').slice(0, 19),
                  level: 'done',
                  text: `「${c.formula || c.material_id}」闯完全关入库` +
                    (c.tc?.tc_K != null ? ` · Tc ${Number(c.tc.tc_K).toFixed(2)} K` : '')});
    }
    items.sort((a, b) => (b.ts || '').localeCompare(a.ts || ''));
    return items.slice(0, 40).map(it => ({
      ...it,
      t: !it.ts ? '' : (it.ts.slice(0, 10) === today
        ? it.ts.slice(11, 19) : it.ts.slice(5, 16).replace('T', ' ')),
    }));
  }, [events, rejectData, winnersData]);

  const elapsed = ov?.run_timing_all?.started_at
    ? (() => {
        const ms = new Date(ov.run_timing_all.finished_at || now).getTime() -
                   new Date(ov.run_timing_all.started_at).getTime();
        const m = Math.floor(ms / 60000);
        if (m < 60) return `${m}分`;
        const hh = Math.floor(m / 60);
        if (hh < 24) return `${hh}时${m % 60}分`;
        return `${Math.floor(hh / 24)}天${hh % 24}时`;
      })() : '—';

  return (
    <div className="sc-root">
      <div className="sc-header">
        <div className="sc-title">
          <span className="sc-title-main">SUPERCOND / 星环实验场</span>
          <span className="sc-title-sub">SC-RING · STRUCTURE ROTATION FIELD</span>
        </div>
        <div className="sc-stats">
          {[['候选总量', total, ''], ['在环筛选', runningN, 'blue'],
            ['已淘汰', Object.values(rejectAgg).reduce((a, b) => a + b, 0), 'red'],
            ['已入库', statusBy.done || 0, 'gold'], ['总时长', elapsed, '']]
            .map(([lbl, v, cls]) => (
            <div className="sc-stat" key={lbl}>
              <i className={cls || ''}>{v}</i>
              <em>{lbl}</em>
            </div>))}
        </div>
        <label className="sc-demo">
          <input type="checkbox" checked={demo}
                 onChange={e => setDemo(e.target.checked)} />
          演示模式
        </label>
      </div>

      <div className="sc-stage">
        <canvas ref={canvasRef} className="sc-canvas" onClick={onCanvasClick}
                onMouseMove={e => {
                  const r = canvasRef.current.getBoundingClientRect();
                  mouseRef.current = {x: e.clientX - r.left,
                                      y: e.clientY - r.top};
                }}
                onMouseLeave={() => { mouseRef.current = null; }} />
        <div className="sc-hint">
          散点 = 候选池（悬停看化学式 / 点击看 3D） ·
          点亮飞入主环筛选 · 中央 CORE 为入库舱
        </div>

        <div className="sc-panel sc-vault">
          <h3>◆ VAULT · 入库舱 <b>{statusBy.done ?? '—'}</b></h3>
          <div className="sc-vault-list">
            {vault.map((v, i) => (
              <div key={v.id}
                   className={`sc-vault-row${freshIds.includes(v.id) ? ' fresh' : ''}`}>
                <span className="idx">{String(i + 1).padStart(2, '0')}</span>
                <span className="f">{v.formula}</span>
                <span className="tc">{v.tc != null
                  ? `Tc ${Number(v.tc).toFixed(2)} K` : '—'}</span>
                {v.lam != null && <span className="lam">λ {Number(v.lam).toFixed(2)}</span>}
              </div>))}
            {!vault.length && <p className="empty">尚无入库记录</p>}
          </div>
        </div>

        {/* 左下：9 关流水卡（风格对齐 VAULT 入库舱） */}
        <div className="sc-panel sc-stages">
          <h3>◇ PIPELINE · 筛选关卡</h3>
          <div className="sc-stage-list">
            {STAGES.map((s, i) => {
              const rc = rejectAgg[i] || 0;
              const n = orbitRef.current &&
                [...orbitRef.current.values()]
                  .filter(p => Math.round(p.targetSi) === i).length;
              return (
                <div className="sc-stage-row" key={s}
                     title={`${STAGE_HUMAN[s]} · 当前在环 ${n || 0} · 累计淘汰 ${rc}`}>
                  <span className="nm">{STAGE_SHORT[i]}</span>
                  <span className="cnt">{n ? `在环 ${n}` : ''}</span>
                  <span className="rc">{rc > 0 ? `淘汰 ${rc}` : ''}</span>
                </div>);
            })}
          </div>
        </div>

        <div className="sc-panel sc-feed">
          <h3>◈ LIVE STREAM · 实验场事件流</h3>
          <div className="sc-feed-list">
            {feed.map(it => (
              <div className="sc-feed-row" key={it.key}>
                <span className="t">{it.t}</span>
                <span className={`lv ${it.level}`}>{it.text}</span>
              </div>))}
            {!feed.length && <p className="empty">等待信号…</p>}
          </div>
        </div>
      </div>

      <div className="sc-bottom">
        <div className="sc-monitors">
          {/* GPU 机监控（ssh nvidia-smi，后端 15s 缓存） */}
          <div className="sc-mon" title="GPU 机器（17501）：AI 模型推理">
            <h4>GPU CLUSTER <i className={cluster?.gpu?.online ? 'ok' : 'bad'}>
              {cluster?.gpu?.online ? 'ONLINE' : 'OFFLINE'}</i></h4>
            {(cluster?.gpu?.gpus || []).map(g => (
              <div className="sc-mon-row" key={g.index}>
                <span className="lbl">GPU{g.index}</span>
                <div className="bars">
                  <div className="bar">
                    <i style={{width: `${g.util}%`,
                               background: g.util >= 95 ? 'var(--sc-red, #e5534b)'
                                          : 'var(--sc-cyan)'}} />
                  </div>
                  <div className="bar thin">
                    <i style={{width: `${g.mem_total ? 100 * g.mem_used / g.mem_total : 0}%`,
                               background: 'var(--sc-gold)'}} />
                  </div>
                </div>
                <span className="val">{g.util}%</span>
                <span className="val dim">{Math.round(g.mem_used / 1024)}/
                  {Math.round(g.mem_total / 1024)}G</span>
              </div>))}
            {cluster?.gpu?.online && !cluster?.gpu?.gpus?.length &&
              <p className="empty">无 GPU 数据</p>}
            {!cluster?.gpu?.online &&
              <p className="empty" style={{color: 'rgba(255,147,140,.7)'}}>
                GPU 机连接不上</p>}
          </div>
          {/* K8s CPU 集群监控（VASP/QE 第一性原理） */}
          <div className="sc-mon" title="K8s CPU 集群：VASP / QE 第一性原理计算">
            <h4>K8S CPU FARM <i className={cluster?.k8s_nodes?.some(n => n.ready)
              ? 'ok' : 'bad'}>
              {cluster?.k8s_nodes?.some(n => n.ready) ? 'READY' : 'DOWN'}</i></h4>
            {(cluster?.k8s_nodes || []).map((n, i) => (
              <div className="sc-mon-row" key={n.name}>
                <span className="lbl">ND{i + 1}</span>
                <div className="bars">
                  <div className="bar">
                    <i style={{width: `${n.cpu_pct || 0}%`,
                               background: 'var(--sc-cyan)'}} />
                  </div>
                  <div className="bar thin">
                    <i style={{width: `${n.mem_pct || 0}%`,
                               background: 'var(--sc-gold)'}} />
                  </div>
                </div>
                <span className="val">{n.cpu_pct != null ? `${n.cpu_pct}%` : '—'}</span>
                <span className="val dim">{n.cpu_used != null
                  ? `${n.cpu_used?.toFixed(1)}/${n.cpu_cap}核` : ''}</span>
              </div>))}
          </div>
          {/* 计算任务池水位 */}
          <div className="sc-mon" title="计算 job 池：case/chunk 计算任务并发水位">
            <h4>JOB POOL</h4>
            <div className="sc-pool">
              <i className="big">{cluster?.jobs?.compute ?? '—'}</i>
              <span className="of">/ {cluster?.max_parallel_jobs ?? '…'} slots</span>
            </div>
            <div className="bar">
              <i style={{width: `${cluster?.jobs?.compute != null &&
                                      cluster?.max_parallel_jobs
                ? Math.min(100, 100 * cluster.jobs.compute /
                           cluster.max_parallel_jobs) : 0}%`,
                background: 'linear-gradient(90deg, var(--sc-cyan), var(--sc-gold))'}} />
            </div>
            <div className="sc-pool-sub">
              mgmt 任务 {cluster?.jobs?.mgmt ?? '—'} ·
              在环 {runningN} 结构
            </div>
          </div>
        </div>
      </div>

      {modal && <StructureModal m={modal} onClose={() => setModal(null)} />}
    </div>
  );
}
